import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Card as RdsCard, InputText, Typography, SectionCard } from "@/deps/ui-library";
import { useHelpHome, useHelpSearch } from "@/api/queries";
import { EmptyState } from "@/components/EmptyState";
import { HelpGate } from "./HelpGate";
import { Excerpt } from "./Excerpt";

/** A link that holds an RDS card: the focus ring is the link's, drawn around the card. */
const cardLink =
  "block h-full rounded-lg no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid";

/** Guides: search what this reader may open, the topics, and the recommended list. */
export function HelpPage() {
  return (
    <HelpGate section="guides">
      <HelpHome />
    </HelpGate>
  );
}

function HelpHome() {
  const [query, setQuery] = useState("");
  const searching = query.trim().length > 0;
  const home = useHelpHome();
  const search = useHelpSearch(query.trim());

  return (
    <div className="flex flex-col gap-4">
      <InputText
        type="search"
        label="Search the guides"
        value={query}
        placeholder="For example: record a receipt"
        onChange={(event) => setQuery(event.target.value)}
      />

      {searching ? (
        <section aria-label="Search results" className="flex flex-col gap-2">
          {search.data?.length === 0 ? (
            <EmptyState
              icon="SearchX"
              title="No guide matches that search"
              description="Try fewer or different words. Guides you may not open are not searched."
            />
          ) : null}
          {search.data?.map((hit) => (
            // Each hit goes to a guide, so it is a link — which may hold the card and its heading.
            <Link
              key={hit.slug}
              to="/help/articles/$slug"
              params={{ slug: hit.slug }}
              className={cardLink}
            >
              <RdsCard variant="interactive" size="sm" className="flex flex-col gap-0.5">
                <Typography.Text as="span" variant="small" muted>
                  {hit.categoryName}
                </Typography.Text>
                <RdsCard.Title size="sm" as="h2">
                  {hit.title}
                </RdsCard.Title>
                <RdsCard.Description size="sm" className="leading-relaxed">
                  <Excerpt
                    text={hit.excerpt}
                    markClassName="text-foreground bg-transparent font-semibold"
                  />
                </RdsCard.Description>
              </RdsCard>
            </Link>
          ))}
        </section>
      ) : null}

      {!searching && home.isError ? (
        <EmptyState
          icon="BookX"
          title="The guides could not be loaded"
          description={(home.error as Error).message}
        />
      ) : null}

      {!searching && home.data ? (
        <>
          {home.data.categories.length === 0 ? (
            <EmptyState
              icon="BookOpen"
              title="No guides published yet"
              description="Guides Rigo publishes for Rigo Accounting appear here, grouped by topic."
            />
          ) : (
            <section
              aria-label="Topics"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              {home.data.categories.map((category) => (
                <Link
                  key={category.id}
                  to="/help/categories/$slug"
                  params={{ slug: category.slug }}
                  className={cardLink}
                >
                  <RdsCard variant="interactive" size="sm" className="flex h-full flex-col">
                    <RdsCard.Title size="sm" as="h2">
                      {category.name}
                    </RdsCard.Title>
                    {category.description ? (
                      <RdsCard.Description size="sm" className="mt-1">
                        {category.description}
                      </RdsCard.Description>
                    ) : null}
                    <Typography.Text as="span" variant="small" muted className="num mt-2">
                      {category.articleCount} {category.articleCount === 1 ? "guide" : "guides"}
                    </Typography.Text>
                  </RdsCard>
                </Link>
              ))}
            </section>
          )}

          {home.data.recommended.length > 0 ? (
            <SectionCard title="Recommended" flush>
              <ul className="flex flex-col">
                {home.data.recommended.map((article) => (
                  <li key={article.id} className="border-border border-b last:border-0">
                    <Link
                      to="/help/articles/$slug"
                      params={{ slug: article.slug }}
                      className="hover:bg-muted focus-visible:ring-ring flex flex-col px-4 py-2.5 no-underline focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                    >
                      <span className="text-foreground text-sm">{article.title}</span>
                      <span className="text-muted-foreground text-xs">
                        {article.categoryName} · {article.sectionName}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </SectionCard>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
